// @view/panels/workspace/AdminPlanPage.panel.tsx
import React from "react";
import EmptyView from "@view/EmptyView";
import * as styles from "@styles/AdminPlan.css";

export type AdminPlanOverviewPhase = "error" | "empty" | "summary";
export type AdminPlanBillingPhase = "hidden" | "empty" | "table";

export type AdminPlanSummaryItem = {
  label: string;
  value: string;
};

export type AdminPlanBillingRow = {
  id: string;
  billedAt: string;
  card: string;
  amount: string;
  provider: string;
  transactionState: string;
  transactionStateClassName: string;
  retryCount: string;
  nextRetry: string;
};

type AdminPlanPagePanelProps = {
  summaryItems: AdminPlanSummaryItem[];
  overviewPhase: AdminPlanOverviewPhase;
  overviewErrorMessage: string;
  billingPhase: AdminPlanBillingPhase;
  billingRows: AdminPlanBillingRow[];
};

const AdminPlanPagePanel: React.FC<AdminPlanPagePanelProps> = ({
  summaryItems,
  overviewPhase,
  overviewErrorMessage,
  billingPhase,
  billingRows,
}) => {
  const overviewView = {
    error: (
      <div className={styles.errorBanner} data-testid="admin-plan-overview-error">
        {overviewErrorMessage}
      </div>
    ),
    empty: (
      <div className={styles.emptyState} data-testid="admin-plan-overview-empty">
        No plan details available yet.
      </div>
    ),
    summary: (
      <div className={styles.planGrid} data-testid="admin-plan-summary">
        {summaryItems.map((item) => (
          <div key={item.label} className={styles.planCard} data-testid="admin-plan-summary-item">
            <div className={styles.planValue}>{item.value}</div>
            <div className={styles.planLabel}>{item.label}</div>
          </div>
        ))}
      </div>
    ),
  } as const satisfies Record<AdminPlanOverviewPhase, React.ReactElement>;

  const billingTableView = (
    <div className={styles.billingTable}>
      <div className={styles.billingHeader}>
        <span>Date</span>
        <span>Card</span>
        <span>Amount</span>
        <span>Provider</span>
        <span>Status</span>
        <span>Retry</span>
        <span>Next retry</span>
      </div>
      {billingRows.map((event) => (
        <div key={event.id} className={styles.billingRow}>
          <span>{event.billedAt}</span>
          <span>{event.card}</span>
          <span>{event.amount}</span>
          <span>{event.provider}</span>
          <span className={event.transactionStateClassName}>
            {event.transactionState}
          </span>
          <span>{event.retryCount}</span>
          <span>{event.nextRetry}</span>
        </div>
      ))}
    </div>
  );

  const billingView = {
    hidden: <EmptyView />,
    empty: (
      <div className={styles.emptyState} data-testid="admin-plan-billing-empty">
        No billing events yet.
      </div>
    ),
    table: <div data-testid="admin-plan-billing-table">{billingTableView}</div>,
  } as const satisfies Record<AdminPlanBillingPhase, React.ReactElement>;

  return (
    <div className={styles.container} data-testid="page-adminPlan" data-page-state="ready">
      <header className={styles.header}>
        <p className={styles.kicker}>Administration</p>
        <h1 className={styles.title}>Manage Plan</h1>
        <p className={styles.subtitle}>
          Review your current subscription tier, billing cadence, and seat
          capacity.
        </p>
      </header>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2 className={styles.panelTitle}>Plan overview</h2>
            <p className={styles.panelHint}>
              Billing details are visible to super admins only.
            </p>
          </div>
        </div>
        <div className={styles.panelBody}>{overviewView[overviewPhase]}</div>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2 className={styles.panelTitle}>Billing history</h2>
            <p className={styles.panelHint}>
              Latest charges and retry state for this subscription.
            </p>
          </div>
        </div>
        <div className={styles.panelBody}>{billingView[billingPhase]}</div>
      </section>
    </div>
  );
};

export default AdminPlanPagePanel;
